/**
 * Watching TV (owner, Sep 25: "new action: watch tv (inside houses where there is a tv) -> it pulls random commercials and
 * nature documentary, the player automatically goes through 4-7 channels (plays 30-100% of the video) and says "there's
 * nothing good on tv right now", and turns it off").
 *
 * F beside a TV (an indoor prop tagged `tv`, in any house or flat) starts a *surf*: a seed and a start time on the person
 * (`Person.tv`, on the wire as `tv`). Everything else is derived from the seed by `surfPlan` — the same on the server, the
 * watcher's browser and anyone else in the room: 4–7 channels up the dial, each a random TV clip (content/media with
 * `show: "tv"` — commercials for the city's brands, nature documentaries, the odd news or cooking bit) played for 30–100 %
 * of its length with a little static between, then the screen goes dark and the character says the line. E, F, walking
 * off or the overlay's button turns it off early. Pure functions here; game.ts steps it, tv-view.ts draws it.
 */
import { Rng } from '../world/rng';

/** the prop tag that makes a piece of furniture a TV (content/props tv_stand, tv_wall) */
export const TV_TAG = 'tv';
/** what the character says when the surf runs out */
export const TV_LINE = "There's nothing good on TV right now.";
/** how near the TV's tile centre you must stand for F to turn it on (m) */
export const TV_REACH = 1.8;
/** channels a surf goes through */
export const SURF_MIN = 4, SURF_MAX = 7;
/** the part of a clip played before the thumb moves on */
export const PLAY_MIN = 0.3, PLAY_MAX = 1;
/** seconds of static when the channel changes (and before the first) */
export const STATIC_S = 0.6;
/** seconds of the dark screen with the line before the surf is over */
export const LINE_S = 3.2;
/** a clip without a length (a script missing `seconds`) plays as if it were this long */
export const DEFAULT_CLIP_S = 20;

/** a clip the TV can show: its media id and its script's length */
export interface TvClip { id: string; seconds: number }
/** one channel of a surf: the clip, the number on the dial, when it starts after the surf began and how long it plays */
export interface SurfStep { clip: string; channel: number; start: number; secs: number; /** the clip's full length (the part played is secs / length) */ length: number }
export interface SurfPlan { steps: SurfStep[]; /** the last clip ends: the screen goes dark, the line is said */ lineAt: number; /** the TV is off */ total: number }
/** a surf in progress, as the person carries it: the TV's tile, the seed and the game time it started */
export interface TvWatch { x: number; y: number; seed: number; at: number; /** (owner, Sep 28) the game console is on the set instead of the channels: its menu ('menu') or the game being played (arcade/index.ts) */ game?: string }

/** the TV clips of the content, in a stable order (the plan picks by index, so every side must list them alike) */
export function tvClips(media: readonly { id: string; show: string; seconds?: number }[]): TvClip[] {
  return media.filter((m) => m.show === 'tv').map((m) => ({ id: m.id, seconds: m.seconds ?? DEFAULT_CLIP_S })).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The surf a seed makes: 4–7 channels climbing the dial, a clip each (no clip twice while there are enough), 30–100 % of it. */
export function surfPlan(seed: number, clips: readonly TvClip[]): SurfPlan {
  const rng = new Rng(seed >>> 0);
  const n = rng.int(SURF_MIN, SURF_MAX);
  const steps: SurfStep[] = [];
  let channel = rng.int(2, 30), t = 0;
  const used = new Set<string>();
  for (let i = 0; i < n; i++) {
    let clip: TvClip | undefined;
    if (clips.length) { const fresh = clips.filter((c) => !used.has(c.id)); clip = rng.pick(fresh.length ? fresh : clips); used.add(clip.id); }
    const length = Math.max(1, clip?.seconds ?? 4);
    const secs = Math.round(length * (PLAY_MIN + (PLAY_MAX - PLAY_MIN) * rng.float()) * 10) / 10;
    t += STATIC_S;
    steps.push({ clip: clip?.id ?? '', channel, start: t, secs, length });
    t += secs;
    channel += rng.int(1, 6); // (the thumb on CH+: up the dial, now and then skipping a few)
  }
  const lineAt = t + STATIC_S * 0.5;
  return { steps, lineAt, total: lineAt + LINE_S };
}

/** what the screen shows `t` seconds into a surf */
export type SurfNow =
  | { phase: 'static'; /** the channel it is changing to */ step: number }
  | { phase: 'clip'; step: number; /** seconds into the clip */ clipT: number }
  | { phase: 'line' }
  | { phase: 'off' };
export function surfAt(plan: SurfPlan, t: number): SurfNow {
  if (t >= plan.total) return { phase: 'off' };
  if (t >= plan.lineAt) return { phase: 'line' };
  for (let i = plan.steps.length - 1; i >= 0; i--) {
    const s = plan.steps[i];
    if (t >= s.start) return t < s.start + s.secs ? { phase: 'clip', step: i, clipT: t - s.start } : { phase: 'static', step: Math.min(plan.steps.length - 1, i + 1) };
  }
  return { phase: 'static', step: 0 };
}

/** the TV within reach of (x, y) among a floor's furniture — the nearest — or nothing; `isTv` says which prop ids are TVs */
export function tvNear(furniture: readonly { x: number; y: number; id: string }[], x: number, y: number, isTv: (id: string) => boolean): { x: number; y: number; id: string } | undefined {
  let best: { x: number; y: number; id: string } | undefined, bd = TV_REACH;
  for (const f of furniture) { if (!isTv(f.id)) continue; const d = Math.hypot(f.x + 0.5 - x, f.y + 0.5 - y); if (d <= bd) { bd = d; best = f; } }
  return best;
}

/** the content's TV clips, listed once per registry */
let listed: { reg: object; list: TvClip[] } | undefined;
export function tvClipsOf(reg: { all(kind: 'media'): readonly { id: string; show: string; seconds?: number }[] }): TvClip[] { if (listed?.reg !== reg) listed = { reg, list: tvClips(reg.all('media')) }; return listed.list; }

/** a small memo: the plan for a seed (the renderer, the HUD and the overlay all ask every frame) */
const plans = new Map<string, SurfPlan>();
export function planFor(w: TvWatch, clips: readonly TvClip[]): SurfPlan {
  const key = `${w.seed}:${clips.length}`;
  let p = plans.get(key);
  if (!p) { p = surfPlan(w.seed, clips); plans.set(key, p); if (plans.size > 64) plans.delete(plans.keys().next().value!); }
  return p;
}
